import React, { useRef, useState, useEffect, useCallback } from "react";
import "./AutomationsCanvas.css";

// ─── Canvas constants ────────────────────────────────────────────────────────
const DOT_SPACING = 18;
const DOT_RADIUS  = 1.2;
const DOT_COLOR   = "#202525";
const BG_COLOR    = "#0a0f0f";
const MIN_ZOOM    = 0.15;
const MAX_ZOOM    = 4;
const ZOOM_SPEED  = 0.0012;
const SNAP_DIST   = 24;
const MENU_W      = 224;
const MENU_H      = 370; 

// ─── Node type definitions ───────────────────────────────────────────────────
const NTYPES = {
    start: {
        label: "START",     color: "#5eead4",
        w: 76,  baseH: 54,
        hasInput: false, hasOutput: true,  hasCond: false,
    },
    action: {
        label: "ACTION",   color: "#60a5fa",
        w: 240, baseH: 108,
        hasInput: true,  hasOutput: true,  hasCond: false,
    },
    delay: {
        label: "DELAY",    color: "#a78bfa",
        w: 218, baseH: 88,
        hasInput: true,  hasOutput: true,  hasCond: false,
    },
    condition: {
        label: "CONDITION", color: "#34d399",
        w: 234, baseH: 136,
        hasInput: true,  hasOutput: false, hasCond: true,
    },
    end: {
        label: "END",      color: "#f87171",
        w: 192, baseH: 70,
        hasInput: true,  hasOutput: false, hasCond: false,
    },
};

const TYPE_ORDER = ["start", "action", "delay", "condition", "end"];
const TYPE_ICONS = { start: "▶", action: "⚙", delay: "⏱", condition: "◇", end: "■" };
const TYPE_DESC  = {
    start:     "Entry point of the flow",
    action:    "Perform an action on a lead",
    delay:     "Wait before continuing",
    condition: "Branch based on a condition",
    end:       "Terminates the flow",
};

const CONDITION_OPTIONS = [
    { value: "email_open",     label: "Email Open",     desc: "Contact opened an email" },
    { value: "email_click",    label: "Email Click",    desc: "Contact clicked a link in an email" },
    { value: "email_reply",    label: "Email Reply",    desc: "Contact replied to an email" },
    { value: "buying_intent",  label: "Buying Intent",  desc: "Contact showed buying signals" },
];

const NODE_CATEGORIES = [
    { label: "Flow",    types: ["start", "end"] },
    { label: "Logic",   types: ["condition"] },
    { label: "Actions", types: ["action", "delay"] },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
let _uid = 0;
const uid = () => `n${++_uid}`;

// Module-level clipboard — survives flow switches (component remounts due to key={currentFlowId})
let _clipboard = null;

const getNodeH = (node) => {
    const cfg = NTYPES[node.type];
    if (!cfg) return 90;
    if (node.type === "action" && node.actionType === "update_funnel_stage")
        return cfg.baseH + 66;
    return cfg.baseH;
};

const getPortPos = (node, portType) => {
    const cfg = NTYPES[node.type];
    if (!cfg) return { x: node.x, y: node.y };
    const h = getNodeH(node);
    const w = cfg.w;
    switch (portType) {
        case "input":  return { x: node.x,     y: node.y + h / 2 };
        case "output": return { x: node.x + w, y: node.y + h / 2 };
        case "yes":    return { x: node.x + w, y: node.y + h * 0.36 };
        case "no":     return { x: node.x + w, y: node.y + h * 0.68 };
        default:       return { x: node.x + w, y: node.y + h / 2 };
    }
};

const createNode = (type, wx, wy) => {
    const cfg  = NTYPES[type];
    const base = { id: uid(), type, x: wx - cfg.w / 2, y: wy - cfg.baseH / 2 };
    switch (type) {
        case "start":     return { ...base };
        case "action":    return { ...base, actionType: "", updateStatus: "", updatePriority: "" };
        case "delay":     return { ...base, delayDays: 1 };
        case "condition": return { ...base, conditionType: "" };
        case "end":       return { ...base };
        default:          return base;
    }
};

const bezier = (x1, y1, x2, y2) => {
    const cp = Math.abs(x2 - x1) * 0.5 + 20;
    return `M${x1},${y1} C${x1 + cp},${y1} ${x2 - cp},${y2} ${x2},${y2}`;
};

// ─── Flow panel state (localStorage) ─────────────────────────────────────────
const PANEL_STATE_KEY = "crm_flow_panel_state";

const loadPanelState = () => {
    try {
        const raw = localStorage.getItem(PANEL_STATE_KEY);
        if (raw) return JSON.parse(raw);
    } catch {}
    return { items: [] };
};

const removeFlowFromItems = (items, flowId) =>
    items
        .map(item => {
            if (item.type === "flow" && item.id === flowId) return null;
            if (item.type === "folder") return { ...item, flows: item.flows.filter(id => id !== flowId) };
            return item;
        })
        .filter(Boolean);

const addFlowToFolder = (items, folderId, flowId) =>
    items.map(item =>
        item.type === "folder" && item.id === folderId
            ? { ...item, flows: [...item.flows, flowId] }
            : item
    );

// ─── Node content sub-components ────────────────────────────────────────────
const ActionContent = ({ node, update }) => (
    <div className="atc-nc" onPointerDown={e => e.stopPropagation()}>
        <select
            className="atc-nc__sel"
            value={node.actionType ?? ""}
            onChange={e => update(node.id, { actionType: e.target.value, updateStatus: "", updatePriority: "" })}
        >
            <option value="">Select action…</option>
            <option value="add_to_campaign">Add to Campaign</option>
            <option value="update_funnel_stage">Update Funnel Stage</option>
        </select>
        {node.actionType === "update_funnel_stage" && <>
            <select
                className="atc-nc__sel"
                value={node.updateStatus ?? ""}
                onChange={e => update(node.id, { updateStatus: e.target.value })}
            >
                <option value="">Status — no change</option>
                <option value="new">New</option>
                <option value="active">Active</option>
                <option value="won">Won</option>
                <option value="lost">Lost</option>
                <option value="converted">Converted</option>
            </select>
            <select
                className="atc-nc__sel"
                value={node.updatePriority ?? ""}
                onChange={e => update(node.id, { updatePriority: e.target.value })}
            >
                <option value="">Priority — no change</option>
                <option value="hot">Hot</option>
                <option value="warm">Warm</option>
                <option value="cold">Cold</option>
            </select>
        </>}
    </div>
);

const DelayContent = ({ node, update }) => (
    <div className="atc-nc atc-nc--row" onPointerDown={e => e.stopPropagation()}>
        <span className="atc-nc__label">Wait</span>
        <input
            className="atc-nc__num"
            type="number"
            min={1}
            max={365}
            value={node.delayDays ?? 1}
            onChange={e => update(node.id, { delayDays: Math.max(1, parseInt(e.target.value) || 1) })}
        />
        <span className="atc-nc__label">{(node.delayDays ?? 1) === 1 ? "day" : "days"}</span>
    </div>
);

const ConditionContent = ({ node, update, pickerOpen, setPickerOpen }) => {
    const selected = CONDITION_OPTIONS.find(o => o.value === node.conditionType) ?? null;
    return (
        <div className="atc-nc atc-nc--cond" onPointerDown={e => e.stopPropagation()}>
            {selected ? (
                <button
                    className="atc-cond__selected"
                    onClick={() => setPickerOpen(v => !v)}
                >
                    <span className="atc-cond__selected-label">{selected.label}</span>
                    <span className="atc-cond__selected-edit">✎</span>
                </button>
            ) : (
                <button
                    className="atc-cond__add-btn"
                    onClick={() => setPickerOpen(v => !v)}
                >
                    <span className="atc-cond__add-plus">+</span> Add Condition
                </button>
            )}
            <div className="atc-nc__branches">
                <span className="atc-nc__branch atc-nc__branch--yes">YES</span>
                <span className="atc-nc__branch atc-nc__branch--no">NO</span>
            </div>
        </div>
    );
};

const EndContent = () => (
    <div className="atc-nc atc-nc--end">
        <p className="atc-nc__end-text">Flow ends here</p>
    </div>
);

// ─── FlowPanelItem ────────────────────────────────────────────────────────────
const FlowPanelItem = ({
    flow, isActive, onSwitch, onRename, onDelete, canDelete, autoFocus, onAutoFocusDone,
    draggable: isDraggable, isDragging, isDragOver,
    onDragStart, onDragEnd, onDragOver, onDragLeave, onDrop,
}) => {
    const [editing, setEditing] = useState(false);
    const [val, setVal]         = useState(flow.name);
    const inputRef              = useRef(null);

    // Sync label when parent renames it
    useEffect(() => { if (!editing) setVal(flow.name); }, [flow.name, editing]);

    // Auto-enter rename when newly created
    useEffect(() => {
        if (autoFocus) { setEditing(true); setVal(""); }
    }, [autoFocus]);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const commit = () => {
        const trimmed = val.trim();
        if (trimmed && trimmed !== flow.name) {
            onRename(flow.id, trimmed);
        } else {
            setVal(flow.name);
        }
        setEditing(false);
        onAutoFocusDone?.();
    };

    return (
        <div
            className={`atc-fp__item${isActive ? " atc-fp__item--active" : ""}${isDragOver ? " atc-fp__item--dragover" : ""}${isDragging ? " atc-fp__item--dragging" : ""}`}
            draggable={isDraggable || false}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            {isDraggable && (
                <div className="atc-fp__drag-handle">
                    <svg width="8" height="10" viewBox="0 0 8 10" fill="currentColor">
                        <circle cx="2" cy="2" r="1.1"/><circle cx="6" cy="2" r="1.1"/>
                        <circle cx="2" cy="5" r="1.1"/><circle cx="6" cy="5" r="1.1"/>
                        <circle cx="2" cy="8" r="1.1"/><circle cx="6" cy="8" r="1.1"/>
                    </svg>
                </div>
            )}
            {isActive && <div className="atc-fp__item-dot" />}
            {editing ? (
                <input
                    ref={inputRef}
                    className="atc-fp__item-input"
                    value={val}
                    onChange={e => setVal(e.target.value)}
                    onBlur={commit}
                    onKeyDown={e => {
                        if (e.key === "Enter")  { e.preventDefault(); commit(); }
                        if (e.key === "Escape") { setVal(flow.name); setEditing(false); onAutoFocusDone?.(); }
                    }}
                    onPointerDown={e => e.stopPropagation()}
                    placeholder="Flow name…"
                />
            ) : (
                <button
                    className="atc-fp__item-name"
                    onClick={() => onSwitch(flow)}
                    title={flow.name}
                >
                    {flow.name}
                </button>
            )}
            <div className="atc-fp__item-actions">
                {!editing && (
                    <button
                        className="atc-fp__icon-btn"
                        title="Rename"
                        onClick={() => { setEditing(true); setVal(flow.name); }}
                    >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                    </button>
                )}
                <button
                    className={`atc-fp__icon-btn atc-fp__icon-btn--danger${!canDelete ? " atc-fp__icon-btn--disabled" : ""}`}
                    title={canDelete ? "Delete" : "Cannot delete the last flow"}
                    onClick={() => canDelete && onDelete(flow.id)}
                    disabled={!canDelete}
                >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14H6L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/>
                        <path d="M9 6V4h6v2"/>
                    </svg>
                </button>
            </div>
        </div>
    );
};

// ─── FlowFolder ──────────────────────────────────────────────────────────────
const FlowFolder = ({
    folder, flows, activeFlowId,
    onSwitchFlow, onRenameFlow, onDeleteFlow, canDeleteFlow,
    pendingRenameId, onAutoFocusDone,
    onFolderRename, onFolderDelete, onFolderToggle,
    autoFocusRename, onAutoFocusRenameDone,
    isDragOver, onHeaderDragOver, onHeaderDragLeave, onHeaderDrop,
    draggingFlowId, dragOverTarget,
    onFlowDragStart, onFlowDragEnd, onFlowDragOver, onFlowDrop,
}) => {
    const [editingName, setEditingName] = useState(false);
    const [nameVal, setNameVal]         = useState(folder.name);
    const nameInputRef                  = useRef(null);

    useEffect(() => { if (!editingName) setNameVal(folder.name); }, [folder.name, editingName]);

    useEffect(() => {
        if (autoFocusRename) { setEditingName(true); setNameVal(""); }
    }, [autoFocusRename]);

    useEffect(() => {
        if (editingName && nameInputRef.current) {
            nameInputRef.current.focus();
            nameInputRef.current.select();
        }
    }, [editingName]);

    const commitName = () => {
        const trimmed = nameVal.trim();
        if (trimmed && trimmed !== folder.name) onFolderRename(folder.id, trimmed);
        else setNameVal(folder.name);
        setEditingName(false);
        onAutoFocusRenameDone?.();
    };

    return (
        <div className="atc-fp__folder">
            <div
                className={`atc-fp__folder-hd${isDragOver ? " atc-fp__folder-hd--dragover" : ""}`}
                onDragOver={onHeaderDragOver}
                onDragLeave={onHeaderDragLeave}
                onDrop={onHeaderDrop}
            >
                <button
                    className="atc-fp__folder-toggle"
                    onClick={() => onFolderToggle(folder.id)}
                    title={folder.collapsed ? "Expand" : "Collapse"}
                >
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transform: folder.collapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.18s" }}>
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>

                <svg className="atc-fp__folder-icon" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>

                {editingName ? (
                    <input
                        ref={nameInputRef}
                        className="atc-fp__item-input atc-fp__folder-input"
                        value={nameVal}
                        onChange={e => setNameVal(e.target.value)}
                        onBlur={commitName}
                        onKeyDown={e => {
                            if (e.key === "Enter")  { e.preventDefault(); commitName(); }
                            if (e.key === "Escape") { setNameVal(folder.name); setEditingName(false); onAutoFocusRenameDone?.(); }
                        }}
                        placeholder="Folder name…"
                        maxLength={80}
                    />
                ) : (
                    <button
                        className="atc-fp__folder-name"
                        onDoubleClick={() => { setEditingName(true); setNameVal(folder.name); }}
                        onClick={() => onFolderToggle(folder.id)}
                        title={`${folder.name} — double-click to rename`}
                    >
                        {folder.name}
                    </button>
                )}

                <span className="atc-fp__folder-count">{flows.length}</span>

                <div className="atc-fp__folder-actions">
                    {!editingName && (
                        <button
                            className="atc-fp__icon-btn"
                            title="Rename folder"
                            onClick={() => { setEditingName(true); setNameVal(folder.name); }}
                        >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                        </button>
                    )}
                    <button
                        className="atc-fp__icon-btn atc-fp__icon-btn--danger"
                        title="Delete folder — flows return to root"
                        onClick={() => onFolderDelete(folder.id)}
                    >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14H6L5 6"/>
                            <path d="M10 11v6"/><path d="M14 11v6"/>
                            <path d="M9 6V4h6v2"/>
                        </svg>
                    </button>
                </div>
            </div>

            {!folder.collapsed && (
                <div className="atc-fp__folder-body">
                    {flows.length === 0 ? (
                        <p className="atc-fp__folder-empty">No flows — drag one here</p>
                    ) : (
                        flows.map(flow => (
                            <FlowPanelItem
                                key={flow.id}
                                flow={flow}
                                isActive={flow.id === activeFlowId}
                                onSwitch={onSwitchFlow}
                                onRename={onRenameFlow}
                                onDelete={onDeleteFlow}
                                canDelete={canDeleteFlow}
                                autoFocus={flow.id === pendingRenameId}
                                onAutoFocusDone={onAutoFocusDone}
                                draggable={true}
                                isDragging={flow.id === draggingFlowId}
                                isDragOver={dragOverTarget?.type === "folder-flow" && dragOverTarget.id === flow.id}
                                onDragStart={(e) => onFlowDragStart(e, flow.id, folder.id)}
                                onDragEnd={onFlowDragEnd}
                                onDragOver={(e) => onFlowDragOver(e, { type: "folder-flow", id: flow.id, folderId: folder.id })}
                                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) onFlowDragOver(e, null); }}
                                onDrop={(e) => onFlowDrop(e, { type: "folder-flow", id: flow.id, folderId: folder.id })}
                            />
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

// ─── NodeBlock ───────────────────────────────────────────────────────────────
const NodeBlock = ({ node, selected, snapId, updateNodeData }) => {
    // Hooks must come before any early return (rules of hooks)
    const [condPickerOpen, setCondPickerOpen] = useState(false);
    const pickerRef = useRef(null);

    useEffect(() => {
        if (!condPickerOpen) return;
        const close = (e) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setCondPickerOpen(false);
            }
        };
        document.addEventListener("pointerdown", close, true);
        return () => document.removeEventListener("pointerdown", close, true);
    }, [condPickerOpen]);

    const cfg = NTYPES[node.type];
    if (!cfg) return null;
    const h = getNodeH(node);
    const { color, w, hasCond, hasInput, hasOutput } = cfg;

    return (
        <div
            className="atc-node"
            data-node-id={node.id}
            style={{ left: node.x, top: node.y, width: w, height: h }}
        >
            {/* Input port */}
            {hasInput && (
                <div
                    className={`atc-port atc-port--in${snapId === node.id ? " atc-port--snap" : ""}`}
                    data-port="input"
                />
            )}

            {/* Body */}
            <div
                className="atc-node__body"
                style={{
                    border:    `1px solid ${selected ? color : color + "44"}`,
                    boxShadow: selected
                        ? `0 0 0 2.5px ${color}30, 0 6px 28px rgba(0,0,0,0.55)`
                        : "0 4px 20px rgba(0,0,0,0.4)",
                }}
            >
                {node.type === "start" ? (
                    /* Start node — compact, no subheader */
                    <div className="atc-nc atc-nc--start">
                        <span className="atc-nc__start-icon" style={{ color }}>{TYPE_ICONS.start}</span>
                        <span className="atc-nc__start-label" style={{ color }}>START</span>
                    </div>
                ) : (
                    <>
                        {/* Subheader */}
                        <div className="atc-node__hd" style={{ background: color + "14", borderBottomColor: color + "30" }}>
                            <span className="atc-node__type" style={{ color }}>{TYPE_ICONS[node.type]}&nbsp; {cfg.label}</span>
                        </div>
                        {/* Content */}
                        {node.type === "action"    && <ActionContent    node={node} update={updateNodeData} />}
                        {node.type === "delay"     && <DelayContent     node={node} update={updateNodeData} />}
                        {node.type === "condition" && (
                            <ConditionContent
                                node={node}
                                update={updateNodeData}
                                pickerOpen={condPickerOpen}
                                setPickerOpen={setCondPickerOpen}
                            />
                        )}
                        {node.type === "end"       && <EndContent />}
                    </>
                )}
            </div>

            {/* Condition picker popup — sibling of body so it escapes overflow:hidden */}
            {node.type === "condition" && condPickerOpen && (
                <div
                    ref={pickerRef}
                    className="atc-cond__picker"
                    onPointerDown={e => e.stopPropagation()}
                >
                    <p className="atc-cond__picker-title">Condition Type</p>
                    {CONDITION_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            className={`atc-cond__picker-item${node.conditionType === opt.value ? " atc-cond__picker-item--active" : ""}`}
                            onClick={() => {
                                updateNodeData(node.id, { conditionType: opt.value });
                                setCondPickerOpen(false);
                            }}
                        >
                            <span className="atc-cond__picker-item-label">{opt.label}</span>
                            <span className="atc-cond__picker-item-desc">{opt.desc}</span>
                        </button>
                    ))}
                </div>
            )}

            {/* Regular output port */}
            {hasOutput && (
                <div className="atc-port atc-port--out" data-port="output" />
            )}

            {/* Condition YES / NO ports (absolutely positioned on right edge) */}
            {hasCond && (
                <>
                    <div
                        className="atc-port atc-port--cond atc-port--yes"
                        data-port="yes"
                        style={{ top: h * 0.36 - 7 }}
                    />
                    <div
                        className="atc-port atc-port--cond atc-port--no"
                        data-port="no"
                        style={{ top: h * 0.68 - 7 }}
                    />
                </>
            )}
        </div>
    );
};

// ─── NodeInsertMenu ───────────────────────────────────────────────────────────
const NodeInsertMenu = ({ onAdd, onClose, pendingConn }) => (
    <div className="atc-node-menu" onPointerDown={e => e.stopPropagation()}>
        <div className="atc-node-menu__header">
            <span className="atc-node-menu__title">
                {pendingConn ? "Connect to…" : "Add Node"}
            </span>
            <button className="atc-node-menu__close" onClick={onClose}>×</button>
        </div>
        {NODE_CATEGORIES.map(cat => (
            <div key={cat.label} className="atc-node-menu__section">
                <span className="atc-node-menu__cat">{cat.label}</span>
                <div className="atc-node-menu__rows">
                    {cat.types.map(type => {
                        const cfg = NTYPES[type];
                        return (
                            <button key={type} className="atc-node-menu__row" onClick={() => onAdd(type)}>
                                <span className="atc-node-menu__row-icon" style={{ color: cfg.color }}>
                                    {TYPE_ICONS[type]}
                                </span>
                                <div className="atc-node-menu__row-info">
                                    <span className="atc-node-menu__row-name" style={{ color: cfg.color }}>
                                        {cfg.label}
                                    </span>
                                    <span className="atc-node-menu__row-desc">{TYPE_DESC[type]}</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        ))}
    </div>
);

// ─── AddNodePicker (toolbar) ─────────────────────────────────────────────────
const AddNodePicker = ({ onAdd }) => (
    <div className="atc-picker" onPointerDown={e => e.stopPropagation()}>
        {TYPE_ORDER.map(type => {
            const cfg = NTYPES[type];
            return (
                <button key={type} className="atc-picker__btn" style={{ borderColor: cfg.color + "50" }} onClick={() => onAdd(type)}>
                    <span className="atc-picker__icon" style={{ color: cfg.color }}>{TYPE_ICONS[type]}</span>
                    <span className="atc-picker__lbl"  style={{ color: cfg.color + "cc" }}>{cfg.label}</span>
                </button>
            );
        })}
    </div>
);

// ─── Main component ──────────────────────────────────────────────────────────
const AutomationsCanvas = ({
    nodes, setNodes, connections, setConnections,
    flows, currentFlowId, currentFlowName, saveStatus,
    onCreateFlow, onSwitchFlow, onRenameFlow, onDeleteFlow,
}) => {
    const containerRef  = useRef(null);
    const bgCanvasRef   = useRef(null);
    const worldLayerRef = useRef(null);
    const cameraRef     = useRef({ x: 0, y: 0, zoom: 1 });
    const bgRafRef      = useRef(null);
    const iaRef         = useRef({ type: "idle" });
    const nodesRef        = useRef([]);
    const connectionsRef  = useRef([]);
    const didInitRef      = useRef(false);

    // ── History (undo/redo) ───────────────────────────────────────────────────
    const historyRef     = useRef([]);
    const historyIdxRef  = useRef(-1);
    const isUndoRedoRef  = useRef(false);

    // ── Clipboard (copy/paste) — backed by module-level _clipboard so it
    //    persists across flow switches (component remounts on key change)

    const [wire, setWire]               = useState(null);
    const snapIdRef                     = useRef(null);
    const [snapId,    _setSnapId]       = useState(null);
    const setSnapId = useCallback((id) => { snapIdRef.current = id; _setSnapId(id); }, []);

    const selectedIdsRef                = useRef(new Set());
    const [selectedIds, _setSelectedIds] = useState(new Set());
    const setSelectedIds = useCallback((ids) => { selectedIdsRef.current = ids; _setSelectedIds(ids); }, []);

    const [marquee, setMarquee]         = useState(null); // { sx, sy, ex, ey } screen coords

    const [contextMenu, setContextMenu] = useState(null);
    const [nodeMenu,    setNodeMenu]    = useState(null); // { sx, sy, wx, wy, pendingConn }
    const [showPicker,  setShowPicker]  = useState(false);

    // ── Flow panel ────────────────────────────────────────────────────────────
    const [showFlowPanel,          setShowFlowPanel]          = useState(false);
    const [pendingRenameId,        setPendingRenameId]        = useState(null);
    const [panelState,             setPanelState]             = useState(loadPanelState);
    const [pendingFolderRenameId,  setPendingFolderRenameId]  = useState(null);
    const [draggingFlowId,         setDraggingFlowId]         = useState(null);
    const [dragOverTarget,         setDragOverTarget]         = useState(null);

    useEffect(() => { nodesRef.current = nodes; }, [nodes]);
    useEffect(() => { connectionsRef.current = connections; }, [connections]);

    // ── Sync flows → panelState (add new, remove deleted) ────────────────────
    useEffect(() => {
        if (!flows || flows.length === 0) return;
        const flowIds = new Set(flows.map(f => f.id));
        setPanelState(prev => {
            // Remove deleted flows
            let newItems = prev.items
                .map(item => {
                    if (item.type === "flow") return flowIds.has(item.id) ? item : null;
                    if (item.type === "folder") return { ...item, flows: item.flows.filter(id => flowIds.has(id)) };
                    return item;
                })
                .filter(Boolean);

            // Find flows not yet tracked anywhere
            const tracked = new Set();
            newItems.forEach(item => {
                if (item.type === "flow") tracked.add(item.id);
                if (item.type === "folder") item.flows.forEach(id => tracked.add(id));
            });
            const missing = flows.filter(f => !tracked.has(f.id));
            if (missing.length > 0) {
                newItems = [...missing.map(f => ({ type: "flow", id: f.id })), ...newItems];
            }
            return { items: newItems };
        });
    }, [flows]); // eslint-disable-line

    // ── Persist panelState → localStorage ────────────────────────────────────
    useEffect(() => {
        try { localStorage.setItem(PANEL_STATE_KEY, JSON.stringify(panelState)); } catch {}
    }, [panelState]);

    // ── Push a history snapshot (call with the NEW state after each change) ───
    const pushHistory = useCallback((newNodes, newConns) => {
        if (isUndoRedoRef.current) return;
        historyRef.current = historyRef.current.slice(0, historyIdxRef.current + 1);
        historyRef.current.push({
            nodes:       newNodes.map(n => ({ ...n })),
            connections: newConns.map(c => ({ ...c })),
        });
        if (historyRef.current.length > 100) {
            historyRef.current.shift();
        } else {
            historyIdxRef.current++;
        }
    }, []);

    // ── Seed a start node when canvas is first used ───────────────────────────
    useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;
        let initNodes = nodes;
        if (nodes.length === 0) {
            initNodes = [createNode("start", 140, 140)];
            setNodes(initNodes);
        }
        // Seed the history with the initial state
        historyRef.current = [{
            nodes:       initNodes.map(n => ({ ...n })),
            connections: connections.map(c => ({ ...c })),
        }];
        historyIdxRef.current = 0;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Update node data ──────────────────────────────────────────────────────
    const updateNodeData = useCallback((id, updates) => {
        setNodes(prev => prev.map(n => n.id === id ? { ...n, ...updates } : n));
    }, [setNodes]);

    // ── Delete node(s) ────────────────────────────────────────────────────────
    const deleteNodes = useCallback((ids) => {
        const idSet = ids instanceof Set ? ids : new Set([ids]);
        const newNodes = nodesRef.current.filter(n => !idSet.has(n.id));
        const newConns = connectionsRef.current.filter(c => !idSet.has(c.from) && !idSet.has(c.to));
        setNodes(newNodes);
        setConnections(newConns);
        setSelectedIds(new Set());
        setContextMenu(null);
        pushHistory(newNodes, newConns);
    }, [setNodes, setConnections, setSelectedIds, pushHistory]);

    const deleteNode = useCallback((id) => {
        deleteNodes(new Set([id]));
    }, [deleteNodes]);

    // ── Create flow (wraps parent, opens panel, triggers rename) ─────────────
    const handleCreateFlowInPanel = useCallback(async () => {
        setShowFlowPanel(true);
        const newFlow = await onCreateFlow();
        if (newFlow) setPendingRenameId(newFlow.id);
    }, [onCreateFlow]);

    const handleAutoFocusDone = useCallback(() => setPendingRenameId(null), []);

    // ── Folder CRUD ───────────────────────────────────────────────────────────
    const handleFolderRename = useCallback((folderId, name) => {
        setPanelState(prev => ({
            items: prev.items.map(item =>
                item.type === "folder" && item.id === folderId ? { ...item, name } : item
            ),
        }));
    }, []);

    const handleFolderDelete = useCallback((folderId) => {
        setPanelState(prev => {
            const folder = prev.items.find(i => i.type === "folder" && i.id === folderId);
            if (!folder) return prev;
            const rootFlows = folder.flows.map(id => ({ type: "flow", id }));
            const newItems  = prev.items.flatMap(item =>
                item.type === "folder" && item.id === folderId ? rootFlows : [item]
            );
            return { items: newItems };
        });
    }, []);

    const handleFolderToggle = useCallback((folderId) => {
        setPanelState(prev => ({
            items: prev.items.map(item =>
                item.type === "folder" && item.id === folderId
                    ? { ...item, collapsed: !item.collapsed }
                    : item
            ),
        }));
    }, []);

    const handleCreateFolder = useCallback(() => {
        const folderId = `f-${Date.now()}`;
        setPanelState(prev => ({
            items: [...prev.items, { type: "folder", id: folderId, name: "New Folder", collapsed: false, flows: [] }],
        }));
        setPendingFolderRenameId(folderId);
    }, []);

    // ── Drag and drop (HTML5 DnD) ─────────────────────────────────────────────
    const handleFlowDragStart = useCallback((e, flowId, fromFolderId) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("flowId", String(flowId));
        e.dataTransfer.setData("fromFolderId", fromFolderId || "");
        setDraggingFlowId(flowId);
    }, []);

    const handleFlowDragEnd = useCallback(() => {
        setDraggingFlowId(null);
        setDragOverTarget(null);
    }, []);

    const handleFlowDragOver = useCallback((e, target) => {
        if (!e) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        setDragOverTarget(target);
    }, []);

    const handleHeaderDragOver = useCallback((e, folderId) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = "move";
        setDragOverTarget({ type: "folder-header", id: folderId });
    }, []);

    const handleRootDropZoneDragOver = useCallback((e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDragOverTarget({ type: "root-drop-zone" });
    }, []);

    const handleDragLeave = useCallback((e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setDragOverTarget(null);
    }, []);

    const handleFlowDrop = useCallback((e, target) => {
        e.preventDefault();
        e.stopPropagation();
        const dragId       = parseInt(e.dataTransfer.getData("flowId"), 10);
        const fromFolderId = e.dataTransfer.getData("fromFolderId") || null;
        if (!dragId) return;

        setPanelState(prev => {
            const items = prev.items;

            if (target.type === "root-flow" && target.id !== dragId) {
                // Create new folder with both flows, placed at target's position
                const newFolderId = `f-${Date.now()}`;
                const folder = { type: "folder", id: newFolderId, name: "New Folder", collapsed: false, flows: [target.id, dragId] };
                const cleaned = removeFlowFromItems(items, dragId);
                const newItems = cleaned.map(item =>
                    item.type === "flow" && item.id === target.id ? folder : item
                );
                setTimeout(() => setPendingFolderRenameId(newFolderId), 0);
                return { items: newItems };
            }

            if (target.type === "folder-header" || target.type === "folder-flow") {
                const folderId = target.type === "folder-header" ? target.id : target.folderId;
                if (fromFolderId === folderId) return prev; // same folder, no-op
                const cleaned = removeFlowFromItems(items, dragId);
                return { items: addFlowToFolder(cleaned, folderId, dragId) };
            }

            if (target.type === "root-drop-zone") {
                const cleaned = removeFlowFromItems(items, dragId);
                return { items: [...cleaned, { type: "flow", id: dragId }] };
            }

            return prev;
        });

        setDraggingFlowId(null);
        setDragOverTarget(null);
    }, []);

    // ── Keyboard shortcuts ────────────────────────────────────────────────────
    useEffect(() => {
        const onKey = (e) => {
            const ctrl = e.ctrlKey || e.metaKey;

            if (e.key === "Escape") { setNodeMenu(null); setContextMenu(null); setShowPicker(false); setShowFlowPanel(false); return; }

            // ── Undo ─────────────────────────────────────────────────────────
            if (ctrl && e.key === "z" && !e.shiftKey) {
                e.preventDefault();
                if (historyIdxRef.current > 0) {
                    isUndoRedoRef.current = true;
                    historyIdxRef.current--;
                    const snap = historyRef.current[historyIdxRef.current];
                    setNodes(snap.nodes.map(n => ({ ...n })));
                    setConnections(snap.connections.map(c => ({ ...c })));
                    setSelectedIds(new Set());
                    isUndoRedoRef.current = false;
                }
                return;
            }

            // ── Redo (Ctrl+Y or Ctrl+Shift+Z) ────────────────────────────────
            if (ctrl && (e.key === "y" || (e.key === "z" && e.shiftKey))) {
                e.preventDefault();
                if (historyIdxRef.current < historyRef.current.length - 1) {
                    isUndoRedoRef.current = true;
                    historyIdxRef.current++;
                    const snap = historyRef.current[historyIdxRef.current];
                    setNodes(snap.nodes.map(n => ({ ...n })));
                    setConnections(snap.connections.map(c => ({ ...c })));
                    setSelectedIds(new Set());
                    isUndoRedoRef.current = false;
                }
                return;
            }

            // ── Select all ────────────────────────────────────────────────────
            if (ctrl && e.key === "a") {
                e.preventDefault();
                setSelectedIds(new Set(nodesRef.current.map(n => n.id)));
                return;
            }

            // ── Copy ──────────────────────────────────────────────────────────
            if (ctrl && e.key === "c") {
                const selIds = selectedIdsRef.current;
                if (selIds.size === 0) return;
                _clipboard = {
                    nodes:       nodesRef.current.filter(n => selIds.has(n.id)).map(n => ({ ...n })),
                    connections: connectionsRef.current.filter(c => selIds.has(c.from) && selIds.has(c.to)).map(c => ({ ...c })),
                };
                return;
            }

            // ── Paste ─────────────────────────────────────────────────────────
            if (ctrl && e.key === "v") {
                if (!_clipboard) return;
                const OFFSET = 24;
                const idMap = {};
                _clipboard.nodes.forEach(n => { idMap[n.id] = uid(); });
                const pastedNodes = _clipboard.nodes.map(n => ({
                    ...n, id: idMap[n.id], x: n.x + OFFSET, y: n.y + OFFSET,
                }));
                const pastedConns = _clipboard.connections.map(c => ({
                    ...c,
                    id:   `${idMap[c.from]}:${c.fromPort ?? "output"}>${idMap[c.to]}`,
                    from: idMap[c.from],
                    to:   idMap[c.to],
                }));
                const newNodes = [...nodesRef.current, ...pastedNodes];
                const newConns = [...connectionsRef.current, ...pastedConns];
                setNodes(newNodes);
                setConnections(newConns);
                setSelectedIds(new Set(pastedNodes.map(n => n.id)));
                pushHistory(newNodes, newConns);
                // Shift clipboard so the next paste lands further over
                _clipboard = {
                    nodes:       _clipboard.nodes.map(n => ({ ...n, x: n.x + OFFSET, y: n.y + OFFSET })),
                    connections: _clipboard.connections,
                };
                return;
            }

            // ── Delete / Backspace ────────────────────────────────────────────
            if (e.key !== "Delete" && e.key !== "Backspace") return;
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
            if (selectedIdsRef.current.size > 0) deleteNodes(selectedIdsRef.current);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [deleteNodes, pushHistory, setNodes, setConnections, setSelectedIds]);

    // ── Camera ────────────────────────────────────────────────────────────────
    const applyCamera = useCallback(() => {
        if (!worldLayerRef.current) return;
        const { x, y, zoom } = cameraRef.current;
        worldLayerRef.current.style.transform = `translate(${x}px,${y}px) scale(${zoom})`;
    }, []);

    const drawBg = useCallback(() => {
        const canvas = bgCanvasRef.current;
        if (!canvas) return;
        const ctx    = canvas.getContext("2d");
        const { x, y } = cameraRef.current;
        const W = canvas.width, H = canvas.height;
        ctx.clearRect(0, 0, W, H);
        ctx.fillStyle = BG_COLOR;
        ctx.fillRect(0, 0, W, H);
        const col = DOT_SPACING, row = col * (Math.sqrt(3) / 2);
        const ox  = ((x % col) + col) % col;
        const oy  = ((y % row) + row) % row;
        const sr  = -Math.floor(y / row) - 1;
        ctx.fillStyle = DOT_COLOR;
        let r = 0;
        for (let cy = oy - row; cy < H + row; cy += row, r++) {
            const odd = ((sr + r) & 1) === 1;
            for (let cx = ox + (odd ? col / 2 : 0) - col; cx < W + col; cx += col) {
                ctx.beginPath(); ctx.arc(cx, cy, DOT_RADIUS, 0, Math.PI * 2); ctx.fill();
            }
        }
    }, []);

    const scheduleDraw = useCallback(() => {
        if (bgRafRef.current) return;
        bgRafRef.current = requestAnimationFrame(() => {
            bgRafRef.current = null;
            drawBg(); applyCamera();
        });
    }, [drawBg, applyCamera]);

    const handleResize = useCallback(() => {
        const el = containerRef.current, cv = bgCanvasRef.current;
        if (!el || !cv) return;
        const dpr = window.devicePixelRatio || 1;
        const r   = el.getBoundingClientRect();
        cv.width  = r.width  * dpr; cv.height = r.height * dpr;
        cv.style.width = `${r.width}px`; cv.style.height = `${r.height}px`;
        cv.getContext("2d").scale(dpr, dpr);
        scheduleDraw();
    }, [scheduleDraw]);

    useEffect(() => {
        handleResize();
        const ro = new ResizeObserver(handleResize);
        if (containerRef.current) ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [handleResize]);

    // ── Coord helpers ─────────────────────────────────────────────────────────
    const c2c = (cx, cy) => {
        const r = containerRef.current?.getBoundingClientRect() ?? { left: 0, top: 0 };
        return { sx: cx - r.left, sy: cy - r.top };
    };
    const s2w = (sx, sy) => {
        const { x, y, zoom } = cameraRef.current;
        return { wx: (sx - x) / zoom, wy: (sy - y) / zoom };
    };

    // ── Clamp menu position so it stays within container ─────────────────────
    const clampMenu = (rawSx, rawSy) => {
        const rect = containerRef.current?.getBoundingClientRect() ?? { width: 800, height: 600 };
        return {
            sx: Math.min(rawSx, rect.width  - MENU_W - 8),
            sy: Math.min(rawSy, rect.height - MENU_H - 8),
        };
    };

    // ── Insert node (from node menu) ──────────────────────────────────────────
    const handleInsertNode = useCallback((type) => {
        if (!nodeMenu) return;
        const { wx, wy, pendingConn } = nodeMenu;
        const newNode  = createNode(type, wx, wy);
        const newNodes = [...nodesRef.current, newNode];
        setNodes(newNodes);
        let newConns = connectionsRef.current;
        if (pendingConn && NTYPES[type]?.hasInput) {
            const { fromId, fromPort } = pendingConn;
            const connId = `${fromId}:${fromPort}>${newNode.id}`;
            if (!newConns.some(c => c.id === connId)) {
                newConns = [...newConns, { id: connId, from: fromId, to: newNode.id, fromPort }];
                setConnections(newConns);
            }
        }
        setNodeMenu(null);
        setShowPicker(false);
        pushHistory(newNodes, newConns);
    }, [nodeMenu, setNodes, setConnections, pushHistory]);

    // ── Add node (from toolbar picker — centers in viewport) ─────────────────
    const handleAddNodeOfType = useCallback((type) => {
        const el = containerRef.current;
        if (!el) return;
        const { width, height } = el.getBoundingClientRect();
        const { wx, wy } = s2w(width / 2, height / 2);
        const newNodes = [...nodesRef.current, createNode(type, wx, wy)];
        setNodes(newNodes);
        setShowPicker(false);
        pushHistory(newNodes, connectionsRef.current);
    }, [setNodes, pushHistory]);

    // ── Pointer down ──────────────────────────────────────────────────────────
    const handlePointerDown = useCallback((e) => {
        if (e.button !== 0) return;

        // Close flow panel on any canvas interaction (panel itself stops propagation)
        setShowFlowPanel(false);

        // Close menus on any canvas click (unless clicking inside them)
        if (!e.target.closest(".atc-node-menu") && !e.target.closest(".atc-ctx-menu")) {
            setNodeMenu(null);
            setContextMenu(null);
        }

        const { sx, sy } = c2c(e.clientX, e.clientY);

        // Any non-input port → start connecting
        const outPort = e.target.closest("[data-port]:not([data-port='input'])");
        if (outPort) {
            const fromPort = outPort.dataset.port;
            const nid      = outPort.closest("[data-node-id]")?.dataset?.nodeId;
            const node     = nodesRef.current.find(n => n.id === nid);
            if (node) {
                const src      = getPortPos(node, fromPort);
                const { wx, wy } = s2w(sx, sy);
                iaRef.current  = { type: "connecting", fromId: nid, fromPort, fromX: src.x, fromY: src.y };
                setWire({ x1: src.x, y1: src.y, x2: wx, y2: wy });
            }
            e.currentTarget.setPointerCapture(e.pointerId);
            return;
        }

        // Input port → drop target only
        if (e.target.closest("[data-port='input']")) return;

        // Node body → drag + select
        const nodeEl = e.target.closest("[data-node-id]");
        if (nodeEl) {
            const nid  = nodeEl.dataset.nodeId;
            const node = nodesRef.current.find(n => n.id === nid);
            if (node) {
                let dragIds;
                if (e.shiftKey) {
                    // Shift+click toggles the node in/out of selection
                    const next = new Set(selectedIdsRef.current);
                    if (next.has(nid)) { next.delete(nid); } else { next.add(nid); }
                    setSelectedIds(next);
                    dragIds = next;
                } else if (selectedIdsRef.current.has(nid)) {
                    // Already in multi-select: drag the whole group
                    dragIds = selectedIdsRef.current;
                } else {
                    // Plain click: single-select
                    const single = new Set([nid]);
                    setSelectedIds(single);
                    dragIds = single;
                }
                setShowPicker(false);

                // Snapshot start positions of all nodes that will be dragged
                const startPositions = {};
                nodesRef.current.forEach(n => {
                    if (dragIds.has(n.id)) startPositions[n.id] = { x: n.x, y: n.y };
                });
                iaRef.current = { type: "drag-node", ids: dragIds, startPositions, startSX: sx, startSY: sy };
            }
            e.currentTarget.setPointerCapture(e.pointerId);
            containerRef.current?.classList.add("atc--moving");
            return;
        }

        // Empty canvas
        setShowPicker(false);
        if (e.shiftKey) {
            // Shift+drag → marquee selection
            iaRef.current = { type: "marquee", startSX: sx, startSY: sy };
            e.currentTarget.setPointerCapture(e.pointerId);
        } else {
            // Plain drag → pan (clear selection)
            setSelectedIds(new Set());
            iaRef.current = { type: "pan", lastX: sx, lastY: sy };
            e.currentTarget.setPointerCapture(e.pointerId);
            containerRef.current?.classList.add("atc--grabbing");
        }
    }, [setSelectedIds]);

    // ── Pointer move ──────────────────────────────────────────────────────────
    const handlePointerMove = useCallback((e) => {
        const { sx, sy } = c2c(e.clientX, e.clientY);
        const ia = iaRef.current;

        if (ia.type === "pan") {
            cameraRef.current.x += sx - ia.lastX;
            cameraRef.current.y += sy - ia.lastY;
            ia.lastX = sx; ia.lastY = sy;
            scheduleDraw(); return;
        }

        if (ia.type === "drag-node") {
            const { zoom } = cameraRef.current;
            const dx = (sx - ia.startSX) / zoom;
            const dy = (sy - ia.startSY) / zoom;
            const moved = nodesRef.current.map(n => {
                const start = ia.startPositions[n.id];
                if (!start) return n;
                return { ...n, x: start.x + dx, y: start.y + dy };
            });
            ia.lastNodes = moved;
            setNodes(moved);
            return;
        }

        if (ia.type === "marquee") {
            setMarquee({ sx: ia.startSX, sy: ia.startSY, ex: sx, ey: sy });
            return;
        }

        if (ia.type === "connecting") {
            const { wx, wy } = s2w(sx, sy);
            const all  = nodesRef.current;
            let snap   = null;
            for (const node of all) {
                if (node.id === ia.fromId) continue;
                if (!NTYPES[node.type]?.hasInput) continue;
                const ip = getPortPos(node, "input");
                if (Math.hypot(wx - ip.x, wy - ip.y) < SNAP_DIST) { snap = node.id; break; }
            }
            setSnapId(snap);
            if (snap) {
                const sn = all.find(n => n.id === snap);
                const ip = getPortPos(sn, "input");
                setWire({ x1: ia.fromX, y1: ia.fromY, x2: ip.x, y2: ip.y });
            } else {
                setWire({ x1: ia.fromX, y1: ia.fromY, x2: wx, y2: wy });
            }
        }
    }, [scheduleDraw, setSnapId, setNodes]);

    // ── Pointer up ────────────────────────────────────────────────────────────
    const handlePointerUp = useCallback((e) => {
        const ia = iaRef.current;

        if (ia.type === "connecting") {
            const { sx, sy } = c2c(e.clientX, e.clientY);
            const { wx, wy } = s2w(sx, sy);
            const all = nodesRef.current;
            let snap  = null;
            for (const node of all) {
                if (node.id === ia.fromId) continue;
                if (!NTYPES[node.type]?.hasInput) continue;
                const ip = getPortPos(node, "input");
                if (Math.hypot(wx - ip.x, wy - ip.y) < SNAP_DIST) { snap = node.id; break; }
            }

            if (snap) {
                // Connected to existing node
                const { fromId, fromPort = "output" } = ia;
                if (!connectionsRef.current.some(c => c.from === fromId && c.fromPort === fromPort && c.to === snap)) {
                    const newConns = [...connectionsRef.current, { id: `${fromId}:${fromPort}>${snap}`, from: fromId, to: snap, fromPort }];
                    setConnections(newConns);
                    pushHistory(nodesRef.current, newConns);
                }
                setWire(null); setSnapId(null);
            } else {
                // Dropped on blank space — open node insert menu
                setWire(null); setSnapId(null);
                const { sx: csx, sy: csy } = clampMenu(sx, sy);
                setNodeMenu({
                    sx: csx, sy: csy,
                    wx, wy,
                    pendingConn: { fromId: ia.fromId, fromPort: ia.fromPort ?? "output" },
                });
            }
            containerRef.current?.classList.remove("atc--grabbing", "atc--moving");
            iaRef.current = { type: "idle" };
            return;
        }

        if (ia.type === "drag-node") {
            // Push history only if the node(s) actually moved
            if (ia.lastNodes && (ia.startSX !== undefined)) {
                pushHistory(ia.lastNodes, connectionsRef.current);
            }
            containerRef.current?.classList.remove("atc--grabbing", "atc--moving");
            iaRef.current = { type: "idle" };
            return;
        }

        if (ia.type === "marquee") {
            const { sx, sy } = c2c(e.clientX, e.clientY);
            const { wx: wx1, wy: wy1 } = s2w(ia.startSX, ia.startSY);
            const { wx: wx2, wy: wy2 } = s2w(sx, sy);
            const minWX = Math.min(wx1, wx2), maxWX = Math.max(wx1, wx2);
            const minWY = Math.min(wy1, wy2), maxWY = Math.max(wy1, wy2);
            const hits = new Set(
                nodesRef.current
                    .filter(n => {
                        const cfg = NTYPES[n.type];
                        if (!cfg) return false;
                        const h = getNodeH(n);
                        return n.x < maxWX && n.x + cfg.w > minWX && n.y < maxWY && n.y + h > minWY;
                    })
                    .map(n => n.id)
            );
            setSelectedIds(hits);
            setMarquee(null);
            iaRef.current = { type: "idle" };
            return;
        }

        containerRef.current?.classList.remove("atc--grabbing", "atc--moving");
        iaRef.current = { type: "idle" };
    }, [setSnapId, setConnections, setSelectedIds, pushHistory]);

    // ── Wheel zoom ────────────────────────────────────────────────────────────
    const handleWheel = useCallback((e) => {
        e.preventDefault();
        const { sx, sy } = c2c(e.clientX, e.clientY);
        const cam     = cameraRef.current;
        const newZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, cam.zoom * (1 + -e.deltaY * ZOOM_SPEED)));
        const scale   = newZoom / cam.zoom;
        cam.x = sx - scale * (sx - cam.x);
        cam.y = sy - scale * (sy - cam.y);
        cam.zoom = newZoom;
        applyCamera();
    }, [applyCamera]);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [handleWheel]);

    // ── Right-click ───────────────────────────────────────────────────────────
    const handleContextMenu = useCallback((e) => {
        e.preventDefault();
        const { sx, sy } = c2c(e.clientX, e.clientY);
        const nodeEl = e.target.closest("[data-node-id]");

        if (nodeEl) {
            // Right-click on a node → delete context menu
            setNodeMenu(null);
            setSelectedIds(new Set([nodeEl.dataset.nodeId]));
            setContextMenu({ nodeId: nodeEl.dataset.nodeId, x: sx, y: sy });
        } else {
            // Right-click on canvas → node insert menu
            setContextMenu(null);
            const { wx, wy } = s2w(sx, sy);
            const { sx: csx, sy: csy } = clampMenu(sx, sy);
            setNodeMenu({ sx: csx, sy: csy, wx, wy, pendingConn: null });
        }
    }, [setSelectedIds]);

    const flowList = flows || [];

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="atc-outer">
            <div
                ref={containerRef}
                className="atc-container"
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
                onContextMenu={handleContextMenu}
            >
                {/* Background */}
                <canvas ref={bgCanvasRef} className="atc-bg-canvas" />

                {/* World layer */}
                <div ref={worldLayerRef} className="atc-world-layer">
                    <svg className="atc-svg">
                        {connections.map(conn => {
                            const a = nodes.find(n => n.id === conn.from);
                            const b = nodes.find(n => n.id === conn.to);
                            if (!a || !b) return null;
                            const src = getPortPos(a, conn.fromPort ?? "output");
                            const dst = getPortPos(b, "input");
                            return (
                                <path key={conn.id} className="atc-conn"
                                    d={bezier(src.x, src.y, dst.x, dst.y)} />
                            );
                        })}
                        {wire && (
                            <path
                                className={`atc-conn-wire${snapId ? " atc-conn-wire--snap" : ""}`}
                                d={bezier(wire.x1, wire.y1, wire.x2, wire.y2)}
                            />
                        )}
                    </svg>

                    {nodes.map(node => (
                        <NodeBlock
                            key={node.id}
                            node={node}
                            selected={selectedIds.has(node.id)}
                            snapId={snapId}
                            updateNodeData={updateNodeData}
                        />
                    ))}
                </div>

                {/* ── Flow HUD chip (top-left, hidden when panel is open) ──── */}
                {!showFlowPanel && (
                    <div className="atc-flow-hud" onPointerDown={e => e.stopPropagation()}>
                        <button
                            className="atc-flow-hud__btn"
                            onClick={() => setShowFlowPanel(true)}
                            title="Browse flows"
                        >
                            <svg className="atc-flow-hud__icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="3" y1="6"  x2="21" y2="6"/>
                                <line x1="3" y1="12" x2="21" y2="12"/>
                                <line x1="3" y1="18" x2="21" y2="18"/>
                            </svg>
                            <span className="atc-flow-hud__name">{currentFlowName || "Untitled"}</span>
                            <span className={`atc-flow-hud__dot atc-flow-hud__dot--${saveStatus || "saved"}`} />
                        </button>
                    </div>
                )}

                {/* ── Flow side panel (slides in from left) ───────────────── */}
                <div
                    className={`atc-flow-panel${showFlowPanel ? " atc-flow-panel--open" : ""}`}
                    onPointerDown={e => e.stopPropagation()}
                >
                    <div className="atc-flow-panel__header">
                        <span className="atc-flow-panel__title">Automations</span>
                        <button
                            className="atc-flow-panel__close"
                            onClick={() => setShowFlowPanel(false)}
                            title="Close"
                        >
                            ×
                        </button>
                    </div>

                    <div className="atc-flow-panel__list">
                        {panelState.items.map(item => {
                            if (item.type === "flow") {
                                const flow = flowList.find(f => f.id === item.id);
                                if (!flow) return null;
                                return (
                                    <FlowPanelItem
                                        key={flow.id}
                                        flow={flow}
                                        isActive={flow.id === currentFlowId}
                                        onSwitch={(f) => { onSwitchFlow(f); setShowFlowPanel(false); }}
                                        onRename={onRenameFlow}
                                        onDelete={onDeleteFlow}
                                        canDelete={flowList.length > 1}
                                        autoFocus={flow.id === pendingRenameId}
                                        onAutoFocusDone={handleAutoFocusDone}
                                        draggable={true}
                                        isDragging={flow.id === draggingFlowId}
                                        isDragOver={dragOverTarget?.type === "root-flow" && dragOverTarget.id === flow.id}
                                        onDragStart={(e) => handleFlowDragStart(e, flow.id, null)}
                                        onDragEnd={handleFlowDragEnd}
                                        onDragOver={(e) => handleFlowDragOver(e, { type: "root-flow", id: flow.id })}
                                        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverTarget(null); }}
                                        onDrop={(e) => handleFlowDrop(e, { type: "root-flow", id: flow.id })}
                                    />
                                );
                            }
                            if (item.type === "folder") {
                                const folderFlows = item.flows.map(id => flowList.find(f => f.id === id)).filter(Boolean);
                                return (
                                    <FlowFolder
                                        key={item.id}
                                        folder={item}
                                        flows={folderFlows}
                                        activeFlowId={currentFlowId}
                                        onSwitchFlow={(f) => { onSwitchFlow(f); setShowFlowPanel(false); }}
                                        onRenameFlow={onRenameFlow}
                                        onDeleteFlow={onDeleteFlow}
                                        canDeleteFlow={flowList.length > 1}
                                        pendingRenameId={pendingRenameId}
                                        onAutoFocusDone={handleAutoFocusDone}
                                        onFolderRename={handleFolderRename}
                                        onFolderDelete={handleFolderDelete}
                                        onFolderToggle={handleFolderToggle}
                                        autoFocusRename={item.id === pendingFolderRenameId}
                                        onAutoFocusRenameDone={() => setPendingFolderRenameId(null)}
                                        isDragOver={dragOverTarget?.type === "folder-header" && dragOverTarget.id === item.id}
                                        onHeaderDragOver={(e) => handleHeaderDragOver(e, item.id)}
                                        onHeaderDragLeave={handleDragLeave}
                                        onHeaderDrop={(e) => handleFlowDrop(e, { type: "folder-header", id: item.id })}
                                        draggingFlowId={draggingFlowId}
                                        dragOverTarget={dragOverTarget}
                                        onFlowDragStart={handleFlowDragStart}
                                        onFlowDragEnd={handleFlowDragEnd}
                                        onFlowDragOver={handleFlowDragOver}
                                        onFlowDrop={handleFlowDrop}
                                    />
                                );
                            }
                            return null;
                        })}

                        {draggingFlowId !== null && (
                            <div
                                className={`atc-fp__root-dropzone${dragOverTarget?.type === "root-drop-zone" ? " atc-fp__root-dropzone--active" : ""}`}
                                onDragOver={handleRootDropZoneDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleFlowDrop(e, { type: "root-drop-zone" })}
                            >
                                Drop here to move to root
                            </div>
                        )}
                    </div>

                    <div className="atc-flow-panel__footer">
                        <button className="atc-flow-panel__new-btn" onClick={handleCreateFlowInPanel}>
                            <span className="atc-flow-panel__new-plus">+</span>
                            New Flow
                        </button>
                        <button className="atc-flow-panel__new-btn atc-flow-panel__new-folder-btn" onClick={handleCreateFolder}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                            </svg>
                            New Folder
                        </button>
                    </div>
                </div>

                {/* Node delete context menu (right-click on node) */}
                {contextMenu && (
                    <div
                        className="atc-ctx-menu"
                        style={{ left: contextMenu.x, top: contextMenu.y }}
                        onPointerDown={e => e.stopPropagation()}
                    >
                        <button
                            className="atc-ctx-menu__item atc-ctx-menu__item--danger"
                            onClick={() => deleteNode(contextMenu.nodeId)}
                        >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/>
                                <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                            </svg>
                            Delete node
                        </button>
                    </div>
                )}

                {/* Node insert menu (right-click on canvas OR drag to blank space) */}
                {nodeMenu && (
                    <div
                        className="atc-node-menu-anchor"
                        style={{ position: "absolute", left: nodeMenu.sx, top: nodeMenu.sy, zIndex: 30 }}
                        onPointerDown={e => e.stopPropagation()}
                    >
                        <NodeInsertMenu
                            onAdd={handleInsertNode}
                            onClose={() => setNodeMenu(null)}
                            pendingConn={nodeMenu.pendingConn}
                        />
                    </div>
                )}

                {/* Marquee selection box */}
                {marquee && (
                    <div
                        className="atc-marquee"
                        style={{
                            left:   Math.min(marquee.sx, marquee.ex),
                            top:    Math.min(marquee.sy, marquee.ey),
                            width:  Math.abs(marquee.ex - marquee.sx),
                            height: Math.abs(marquee.ey - marquee.sy),
                        }}
                    />
                )}

                {/* Toolbar area (quick-add picker + add button) */}
                <div className="atc-toolbar-area" onPointerDown={e => e.stopPropagation()}>
                    {showPicker && <AddNodePicker onAdd={handleAddNodeOfType} />}
                    <div className="atc-toolbar">
                        <button
                            className={`atc-add-btn${showPicker ? " atc-add-btn--open" : ""}`}
                            onClick={() => setShowPicker(v => !v)}
                            title="Add node"
                        >
                            {showPicker ? "×" : "+"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AutomationsCanvas;
